import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Job, JobStatus } from "@/lib/mockData";
import { FilterBar } from "@/features/dashboard/FilterBar";
import { JobTile } from "@/features/dashboard/JobTile";
import { AttentionRail } from "@/features/dashboard/AttentionRail";
import { RecentTable } from "@/features/dashboard/RecentTable";
import { QueueJobModal } from "@/features/dashboard/QueueJobModal";
import { UsageStrip } from "@/features/dashboard/UsageStrip";
import { useEntities } from "@/lib/hooks/useEntities";
import { useJobsApi } from "@/lib/hooks/useJobsApi";

export function DashboardScreen() {
  const jobsQuery = useJobsApi();
  const entitiesQuery = useEntities();

  const allJobs = useMemo(() => jobsQuery.data?.items ?? [], [jobsQuery.data]);
  // Real entities only. Until they load the filter shows just "All".
  const entities = useMemo(() => entitiesQuery.data?.items ?? [], [entitiesQuery.data]);
  const [queuedJobId, setQueuedJobId] = useState<string | null>(null);

  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<JobStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [tileSize, setTileSize] = useState<"s" | "m" | "l">("m");
  const [queueOpen, setQueueOpen] = useState(false);

  const statusCounts = useMemo(() => {
    const out: Partial<Record<JobStatus, number>> = {};
    for (const j of allJobs) out[j.status] = (out[j.status] ?? 0) + 1;
    return out;
  }, [allJobs]);

  const filtered = useMemo(
    () => filterJobs(allJobs, { entityFilter, statusFilter, search }),
    [allJobs, entityFilter, statusFilter, search],
  );

  const active = filtered.filter((j) =>
    ["running", "approval", "takeover"].includes(j.status),
  );
  const recent = filtered.filter((j) => !active.includes(j));

  const approvals = allJobs.filter((j) => j.status === "approval");
  const takeovers = allJobs.filter((j) => j.status === "takeover");

  return (
    <div className="grid h-full grid-cols-[1fr_340px] overflow-hidden">
      <div className="flex min-w-0 flex-col overflow-hidden">
        <FilterBar
          entities={entities}
          entityFilter={entityFilter}
          onEntityFilter={setEntityFilter}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          statusCounts={statusCounts}
          search={search}
          onSearch={setSearch}
          tileSize={tileSize}
          onTileSize={setTileSize}
          onQueueJob={() => setQueueOpen(true)}
        />

        <div className="flex-1 overflow-auto p-6" data-tour="dashboard-grid">
          <UsageStrip jobs={allJobs} />
          {jobsQuery.isPending ? (
            <LoadingSection />
          ) : jobsQuery.isError ? (
            <ErrorSection
              error={
                jobsQuery.error instanceof Error ? jobsQuery.error.message : "unknown"
              }
              onRetry={() => jobsQuery.refetch()}
            />
          ) : (
            <>
              <ActiveSection jobs={active} tileSize={tileSize} />
              <RecentSection jobs={recent} />
            </>
          )}
        </div>
      </div>

      <AttentionRail approvals={approvals} takeovers={takeovers} />

      <QueueJobModal
        open={queueOpen}
        onClose={() => setQueueOpen(false)}
        onStarted={setQueuedJobId}
      />

      {queuedJobId && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-[150] flex items-center gap-3 rounded-md border border-paper-line-strong bg-paper-3 px-4 py-2 text-sm text-ink-1 shadow-lg"
        >
          <span>Job queued. It will appear in the list below.</span>
          <Link to={`/jobs/${queuedJobId}`} className="text-teal-700 underline">
            Open job
          </Link>
          <button
            type="button"
            onClick={() => setQueuedJobId(null)}
            className="rounded-sm px-1 text-ink-3 hover:bg-paper-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function LoadingSection() {
  return (
    <section className="mb-6">
      <div className="rounded-md border border-dashed border-paper-line bg-paper-2 p-12 text-center text-sm text-ink-3">
        Loading live jobs from the backend…
      </div>
    </section>
  );
}

function ErrorSection({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <section className="mb-6">
      <div className="rounded-md border border-status-failed/40 bg-status-failed-bg p-6 text-sm">
        <div className="font-medium text-status-failed">Backend unreachable.</div>
        <div className="mt-1 text-ink-2">{error}</div>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-xs text-ink-1 hover:bg-paper-tint"
        >
          Retry
        </button>
      </div>
    </section>
  );
}

function ActiveSection({ jobs, tileSize }: { jobs: Job[]; tileSize: "s" | "m" | "l" }) {
  return (
    <section className="mb-6">
      <div className="mb-3 flex items-baseline gap-2">
        <h2 className="text-md font-medium text-ink-1">Active</h2>
        <span className="font-mono text-xs text-ink-4">{jobs.length}</span>
        <span className="ml-1 inline-flex items-center gap-1.5 rounded-pill bg-status-running-bg px-2 py-0.5 text-[11px] text-status-running">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-status-running" />
          Live
        </span>
        <span className="ml-auto text-xs text-ink-4">Polling every 2s</span>
      </div>

      {jobs.length === 0 ? (
        <div className="rounded-md border border-dashed border-paper-line bg-paper-2 p-12 text-center text-sm text-ink-3">
          Nothing running right now. Hit <strong className="text-ink-1">Queue job</strong> to
          start a test workflow.
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {jobs.map((j) => (
            <JobTile key={j.id} job={j} size={tileSize} />
          ))}
        </div>
      )}
    </section>
  );
}

function RecentSection({ jobs }: { jobs: Job[] }) {
  return (
    <section>
      <div className="mb-3 flex items-baseline gap-2">
        <h2 className="text-md font-medium text-ink-1">Recent</h2>
        <span className="font-mono text-xs text-ink-4">{jobs.length}</span>
      </div>
      <RecentTable jobs={jobs.slice(0, 15)} />
    </section>
  );
}

function filterJobs(
  jobs: Job[],
  { entityFilter, statusFilter, search }: { entityFilter: string; statusFilter: JobStatus | "all"; search: string },
): Job[] {
  const q = search.trim().toLowerCase();
  return jobs.filter((j) => {
    if (entityFilter !== "all" && j.entity.id !== entityFilter) return false;
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (q) {
      const hay = [
        j.id,
        j.workflow.name,
        j.entity.name,
        j.entity.full,
        j.patient?.name ?? "",
        j.patient?.mrn ?? "",
        j.user.name,
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}
